<script setup lang="ts">
import {
    FdxTabs,
    FdxTabPane,
    FdxIntersectionTrigger,
    FdxLoadingSpinner,
} from '@foodics/ui-common';
import {
    groupTypesMap,
    useNotifications,
    type NotificationGroup,
} from '@/composables/notifications';
import { computed, onUnmounted, ref, watchEffect } from 'vue';
import NotificationItem from './NotificationItem.vue';
import { groupBy } from 'lodash-es';
import { format, isToday, isYesterday } from 'date-fns';
import { useLocale } from '@/composables/locale';

const {
    notifications: allNotifications,
    setFilter,
    markAllAsRead,
    isLoading,
    getUnreadCountByType,
    hasNextPage,
    fetchNextPage,
    hasFetchedInitialPage,
} = useNotifications();

const { t } = useLocale();
// Group notifications by date
const groupedNotificationsByDate = computed(() =>
    groupBy(allNotifications.value, (notification) => {
        const notificationDate = new Date(notification.created_at);

        if (isToday(notificationDate)) return t('generic.dates.today');
        if (isYesterday(notificationDate)) return t('generic.dates.yesterday');

        return format(notificationDate, 'dd MMM yyyy');
    }),
);

const tabs: { key: NotificationGroup; label: string }[] = [
    { key: 'transactions', label: t('notifications.tabs.transactions') },
    { key: 'for_you', label: t('notifications.tabs.for_you') },
];

const selectedTab = ref<NotificationGroup>('transactions');
watchEffect(() => {
    setFilter('type', groupTypesMap[selectedTab.value]);
});

onUnmounted(() => {
    markAllAsRead();
});

const onIntersectionTriggered = () => {
    fetchNextPage();
};

const onTabChange = (tab: NotificationGroup) => {
    // mark all as read for the previous tab
    markAllAsRead();
    // set the new tab
    selectedTab.value = tab;
};
</script>

<template>
    <div class="c-notifications-center flex flex-col overflow-hidden">
        <!-- Header -->

        <h2
            class="text-title-3 font-bold text-gray-1000 p-4 border-b border-gray-200"
        >
            {{ $t('notifications.title') }}
        </h2>

        <!-- Tabs -->
        <FdxTabs
            :model-value="selectedTab"
            @update:model-value="onTabChange($event as NotificationGroup)"
            class="flex flex-col overflow-auto"
            :ignore-route-updates="true"
        >
            <FdxTabPane
                v-for="tab in tabs"
                :key="tab.key"
                :name="tab.key"
                :label="tab.label"
                class="overflow-auto"
            >
                <template #label>
                    <span>
                        {{ tab.label }}
                    </span>

                    <span
                        v-if="getUnreadCountByType(groupTypesMap[tab.key]) > 0"
                        class="inline-block ms-0.5 size-2.5 bg-negative-500 rounded-full border-2 border-white"
                        data-testid="nav-notifications-badge"
                    />
                </template>
                <!-- Notifications List -->
                <div class="overflow-y-auto py-4">
                    <template v-if="isLoading && !hasFetchedInitialPage">
                        <FdxLoadingSpinner :show-text="false" class="my-6" />
                    </template>

                    <template v-else-if="allNotifications.length > 0">
                        <div
                            v-for="(
                                notifications, date
                            ) in groupedNotificationsByDate"
                            :key="date"
                            class="flex flex-col"
                        >
                            <!-- Date Group Header -->
                            <div class="mb-2 px-4">
                                <span class="text-caption-1 text-gray-700">
                                    {{ date }}
                                </span>
                            </div>

                            <!-- Notification Items -->
                            <NotificationItem
                                v-for="notification in notifications"
                                :key="notification.id"
                                :notification="notification"
                            />
                        </div>

                        <FdxIntersectionTrigger
                            @intersected="onIntersectionTriggered"
                            :disabled="isLoading || !hasNextPage"
                        >
                            <Transition name="fade" appear>
                                <FdxLoadingSpinner
                                    v-if="isLoading"
                                    :show-text="false"
                                    class="my-6"
                                />

                                <p
                                    v-else-if="!hasNextPage"
                                    class="flex items-center gap-2 text-caption-1 text-gray-700 justify-center my-6"
                                >
                                    <FdxIconCheckCircle class="size-4" />
                                    <span>
                                        {{ $t('notifications.footer') }}
                                    </span>
                                </p>
                            </Transition>
                        </FdxIntersectionTrigger>
                    </template>

                    <!-- Empty State -->
                    <div
                        v-else
                        class="flex flex-col items-center justify-center my-8"
                    >
                        <div
                            class="size-20 flex items-center justify-center bg-gray-100 rounded-full"
                        >
                            <FdxIconNotifications
                                class="size-9 text-gray-700"
                            />
                        </div>

                        <h4 class="text-footnote font-bold text-gray-1000">
                            {{ $t('notifications.empty.title') }}
                        </h4>

                        <p
                            class="text-caption-1 text-gray-700 text-center"
                            v-html="$t('notifications.empty.description')"
                        />
                    </div>
                </div>
            </FdxTabPane>
        </FdxTabs>
    </div>
</template>

<style scoped>
:deep(.tabs-wrapper) {
    @apply px-4;
}
</style>

<style>
.v-popper__inner:has(.c-notifications-center) > div {
    max-height: 600px !important;
    @apply flex;
}
</style>
